import React from "react";
import ReactDOM from "react-dom";

interface FrameProps {
  title: string; // Required title prop
  afterInit?: (document: Document) => void; // Optional callback after iframe is initialized
  style?: React.CSSProperties; // Optional style prop
  customScript?: string; // Optional custom JavaScript to inject
}

class Frame extends React.Component<FrameProps> {
  iframeRef = React.createRef<HTMLIFrameElement>();
  renderTimeout: number | null = null;

  componentDidMount() {
    this.renderFrameContents();
  }

  componentDidUpdate() {
    this.renderFrameContents();
  }

  componentWillUnmount() {
    const iframe = this.iframeRef.current;
    const iframeDoc = iframe?.contentDocument;

    if (iframeDoc) {
      // Step 1: Unmount React components rendered inside the iframe
      const insertPoint = iframeDoc.getElementById("insert-point");
      if (insertPoint) {
        ReactDOM.unmountComponentAtNode(insertPoint);
        // Safely remove insertPoint if it's still attached
        if (insertPoint.isConnected) {
          insertPoint.remove();
        }
      }

      // Step 2: Clear the iframe's body content
      iframeDoc.body.innerHTML = "";

      // Step 3: Remove any dynamically added scripts
      const scripts = iframeDoc.body.querySelectorAll("script");
      scripts.forEach((script) => script.remove());
    }

    // Step 4: Reset the iframe source and remove the iframe element
    if (iframe) {
      iframe.src = "about:blank"; // Reset the iframe source
      // iframe.remove(); // Remove the iframe element from the DOM
    }

    // Step 5: Clear timeout to prevent retained references
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout);
      this.renderTimeout = null;
    }

    // Step 6: Nullify the iframe reference
    // this.iframeRef.current = null;
  }

  renderFrameContents() {
    const iframeDoc = this.iframeRef.current?.contentDocument;
    if (iframeDoc && iframeDoc.readyState === "complete") {
      // Create an insert point in the iframe document
      if (!iframeDoc.getElementById("insert-point")) {
        const insertPoint = iframeDoc.createElement("div");
        insertPoint.id = "insert-point";
        iframeDoc.body.appendChild(insertPoint);
      }

      // Execute afterInit if provided
      if (this.props.afterInit) {
        this.props.afterInit(iframeDoc);
      }

      // Inject custom script if provided
      if (this.props.customScript) {
        const script = iframeDoc.createElement("script");
        script.type = "text/javascript";
        script.text = this.props.customScript;
        iframeDoc.body.appendChild(script);
      }

      // Set iframe body margin to 0
      iframeDoc.body.style.margin = "0";

      // Render React children
      const insertPoint = iframeDoc.getElementById("insert-point");
      if (insertPoint) {
        ReactDOM.render(<>{this.props.children}</>, insertPoint);
      }
    } else {
      // Retry rendering if the iframe document is not ready
      this.renderTimeout = window.setTimeout(() => this.renderFrameContents(), 0);
    }
  }

  render() {
    return <iframe ref={this.iframeRef} title={this.props.title} id={this.props.title} style={this.props.style} />;
  }
}

export default Frame;
